/**
 * Section-Based Scroll Position Manager
 * Uses section IDs for more accurate scroll position restoration
 * Falls back to pixel-based positioning if section detection fails
 */

import { ScrollSmoother } from 'gsap/ScrollSmoother';

const SCROLL_POSITION_KEY = 'aim_scroll_position';
const SCROLL_TIMESTAMP_KEY = 'aim_scroll_timestamp';
const SCROLL_POSITION_TTL = 30000; // 30 seconds TTL for scroll position

/**
 * Get the section ID that the user is currently viewing
 */
const getCurrentSection = () => {
  if (typeof window === 'undefined') return null;

  const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
  const viewportHeight = window.innerHeight;
  const centerOfViewport = scrollPosition + viewportHeight / 2;

  // Define section selectors in order of appearance
  const sectionSelectors = [
    'header',
    '#hero-section',
    '#the-platform-section',
    '#how-it-works-section',
    '#our-clients-section',
    '#dashboard-slider-section',
    // About Us page sections
    '#textSectionWrapper',
    '#about-values-section',
    '#faq-accordion-section',
    '#why-dm-advantages-section',
    '#why-dm-aim360-advantage-section',
    // AIM360 page sections
    '#core-data-compliance-section',
    '#core-data-api-section',
    '#acquisition-performance-section',
    // Industries page sections
    '#property-advantages-section',
    '#property-tabbed-carousel-section',
    '#travel-faq-section',
    '#broadband-intro-section',
    '#broadband-homemover-section',
    '#utilities-intro-section',
    '#utilities-homemover-section',
    '#utilities-faq-section',
    '#charities-intro-section',
    '#charities-faq-section',
    '#charities-trust-section',
    '#insurance-intro-section',
    '#insurance-homemover-section',
    '#insurance-toolbox-section',
    '#senior-living-intro-section',
    '#senior-living-targeting-section',
    '#senior-living-trust-section',
    // Common sections
    '#schedule-your-meeting',
    'footer'
  ];

  // Find the section that contains the center of the viewport
  for (const selector of sectionSelectors) {
    const element = document.querySelector(selector);
    if (element) {
      const rect = element.getBoundingClientRect();
      const elementTop = rect.top + scrollPosition;
      const elementBottom = elementTop + rect.height;

      if (centerOfViewport >= elementTop && centerOfViewport <= elementBottom) {
        return {
          id: element.id || selector,
          selector: selector,
          offset: scrollPosition - elementTop, // Offset within the section
          elementTop: elementTop
        };
      }
    }
  }

  return null;
};

/**
 * Store current scroll position with section information
 */
export const storeScrollPosition = () => {
  if (typeof window === 'undefined') return;

  try {
    const smoother = ScrollSmoother.get();
    let scrollPosition = 0;

    if (smoother) {
      scrollPosition = smoother.scrollTop();
    } else {
      scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
    }

    const currentSection = getCurrentSection();

    const scrollData = {
      position: scrollPosition,
      section: currentSection,
      timestamp: Date.now(),
      pathname: window.location.pathname,
      viewportHeight: window.innerHeight
    };

    sessionStorage.setItem(SCROLL_POSITION_KEY, JSON.stringify(scrollData));

    // Debug logging in development
    if (process.env.NODE_ENV === 'development') {
      console.log('Stored scroll position:', {
        position: scrollPosition,
        section: currentSection?.id || 'unknown',
        offset: currentSection?.offset || 0
      });
    }
  } catch (error) {
    console.warn('Failed to store scroll position:', error);
  }
};

/**
 * Restore scroll position using section-based approach
 */
export const restoreScrollPosition = () => {
  if (typeof window === 'undefined') return;

  try {
    const storedData = sessionStorage.getItem(SCROLL_POSITION_KEY);
    if (!storedData) return;

    const scrollData = JSON.parse(storedData);
    const now = Date.now();
    const isExpired = now - scrollData.timestamp > SCROLL_POSITION_TTL;
    const isSamePage = scrollData.pathname === window.location.pathname;

    // Only restore if data is fresh and from the same page
    if (isExpired || !isSamePage) {
      sessionStorage.removeItem(SCROLL_POSITION_KEY);
      return;
    }

    const restoreWithDelay = (delay = 100) => {
      setTimeout(() => {
        const smoother = ScrollSmoother.get();

        // Try section-based restoration first
        if (scrollData.section && scrollData.section.id) {
          const targetElement = document.querySelector(scrollData.section.selector);

          if (targetElement) {
            const rect = targetElement.getBoundingClientRect();
            const elementTop =
              rect.top + (window.pageYOffset || document.documentElement.scrollTop);
            const targetPosition = elementTop + (scrollData.section.offset || 0);

            if (smoother) {
              try {
                smoother.scrollTo(targetPosition, true);

                if (process.env.NODE_ENV === 'development') {
                  console.log('Restored to section:', {
                    section: scrollData.section.id,
                    offset: scrollData.section.offset,
                    position: targetPosition
                  });
                }

                // Clean up after successful restoration
                sessionStorage.removeItem(SCROLL_POSITION_KEY);
                return;
              } catch (error) {
                console.warn('ScrollSmoother section restoration failed:', error);
              }
            } else {
              // Fallback to native scrolling
              window.scrollTo(0, targetPosition);
              sessionStorage.removeItem(SCROLL_POSITION_KEY);
              return;
            }
          }
        }

        // Fallback to pixel-based restoration
        const targetPosition = scrollData.position;

        if (smoother) {
          try {
            smoother.scrollTo(targetPosition, true);
          } catch (error) {
            console.warn('ScrollSmoother pixel restoration failed:', error);
            window.scrollTo(0, targetPosition);
          }
        } else {
          window.scrollTo(0, targetPosition);
        }

        // Clean up after restoration
        sessionStorage.removeItem(SCROLL_POSITION_KEY);

        if (process.env.NODE_ENV === 'development') {
          console.log('Restored to pixel position:', targetPosition);
        }
      }, delay);
    };

    // Try immediate restoration first
    restoreWithDelay(100);

    // Fallback restoration in case ScrollSmoother takes longer to initialize
    restoreWithDelay(500);
  } catch (error) {
    console.warn('Failed to restore scroll position:', error);
    sessionStorage.removeItem(SCROLL_POSITION_KEY);
  }
};

/**
 * Clear stored scroll position
 */
export const clearScrollPosition = () => {
  if (typeof window === 'undefined') return;

  try {
    sessionStorage.removeItem(SCROLL_POSITION_KEY);
    sessionStorage.removeItem(SCROLL_TIMESTAMP_KEY);
  } catch (error) {
    console.warn('Failed to clear scroll position:', error);
  }
};

/**
 * Initialize section-based scroll position management
 */
export const initializeScrollPositionManager = () => {
  if (typeof window === 'undefined') return;

  // Restore scroll position on page load
  restoreScrollPosition();

  // Store scroll position before page unload (for navigation)
  const handleBeforeUnload = () => {
    storeScrollPosition();
  };

  // Store scroll position periodically while scrolling
  let scrollTimeout;
  const handleScroll = () => {
    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(() => {
      storeScrollPosition();
    }, 150); // Debounce scroll position storage
  };

  window.addEventListener('beforeunload', handleBeforeUnload);
  window.addEventListener('scroll', handleScroll, { passive: true });

  // Cleanup function
  return () => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
    window.removeEventListener('scroll', handleScroll);
    clearTimeout(scrollTimeout);
  };
};
